import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { create } from 'zustand';

import { de, type Messages } from './de';
import { en } from './en';
import { fr } from './fr';

/**
 * Sprache der **Oberfläche**. Prüfungsinhalte bleiben unberührt — die kommen vom Server.
 *
 * Regel des Nutzers: die Sprache des iPads wird übernommen; ist sie keine der drei, gilt
 * Englisch; die Wahl lässt sich in den Einstellungen dauerhaft überschreiben.
 */

export const LOCALES = ['de', 'fr', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const CATALOGS: Record<Locale, Messages> = { de, fr, en };

/** Wo die überschriebene Wahl liegt. */
const STORAGE_KEY = 'ui.locale';

const isLocale = (value: string): value is Locale => (LOCALES as readonly string[]).includes(value);

/**
 * Sprache aus einer BCP-47-Kennung ableiten: `de-CH` → `de`.
 * Alles außerhalb der drei ergibt `en`.
 */
export function localeFromTag(tag: string | undefined | null): Locale {
  const primary = (tag ?? '').toLowerCase().split(/[-_]/)[0];
  return isLocale(primary) ? primary : 'en';
}

interface LocaleState {
  locale: Locale;
  /** Ob die Sprache von Hand gewählt wurde — nur dann wird sie gespeichert. */
  chosen: boolean;
  t: Messages;
  /** Bis `init` gelaufen ist, steht `false` — erst danach ist `chosen` verlässlich. */
  geladen: boolean;
  /** Die Wahl des Prüfers: gilt und wird gespeichert. */
  setLocale: (locale: Locale) => void;
  /**
   * Die Vorgabe aus `language` anzeigen, **ohne** sie als Wahl zu speichern — sonst stünde ein
   * MDM- oder Relay-Wert danach als Entscheidung des Prüfers da (s. `features/sprache.ts`).
   */
  setzeVorgabe: (locale: Locale) => void;
  /** Die gespeicherte Wahl vergessen; es gilt wieder die Vorgabe. */
  vergissWahl: () => void;
  /** Beim Start: gespeicherte Wahl, sonst Gerätesprache. */
  init: () => Promise<void>;
}

/**
 * Die Sprache des iPads, auf eine der drei abgebildet.
 *
 * `Device.getLanguageTag` liest die Systemsprache. `navigator.language` wäre im WKWebView
 * unzuverlässig: dort richtet es sich nach den Lokalisierungen der App, und die hat keine — es
 * käme immer Englisch heraus.
 */
export async function geraetesprache(): Promise<Locale> {
  try {
    const { value } = await Device.getLanguageTag();
    return localeFromTag(value);
  } catch {
    return localeFromTag(typeof navigator === 'undefined' ? 'en' : navigator.language);
  }
}

export const useLocaleStore = create<LocaleState>()((set) => ({
  // Bis `init` gelaufen ist, steht Englisch — der vorgegebene Rückfall.
  locale: 'en',
  chosen: false,
  geladen: false,
  t: en,

  setLocale: (locale) => {
    set({ locale, chosen: true, t: CATALOGS[locale] });
    document.documentElement.lang = CATALOGS[locale].tag;
    void Preferences.set({ key: STORAGE_KEY, value: locale });
  },

  setzeVorgabe: (locale) => {
    set({ locale, t: CATALOGS[locale] });
    document.documentElement.lang = CATALOGS[locale].tag;
  },

  vergissWahl: () => {
    set({ chosen: false });
    void Preferences.remove({ key: STORAGE_KEY }).catch(() => undefined);
  },

  init: async () => {
    let locale: Locale | null = null;
    let chosen = false;

    try {
      const stored = await Preferences.get({ key: STORAGE_KEY });
      if (stored.value && isLocale(stored.value)) {
        locale = stored.value;
        chosen = true;
      }
    } catch {
      // Kein Speicher erreichbar — dann eben die Gerätesprache.
    }

    if (!locale) locale = await geraetesprache();

    set({ locale, chosen, geladen: true, t: CATALOGS[locale] });
    document.documentElement.lang = CATALOGS[locale].tag;
  },
}));

/** Die Texte der aktuellen Sprache. */
export const useT = () => useLocaleStore((s) => s.t);

/**
 * Punktzahl in der Schreibweise der Oberflächensprache.
 *
 * Das Dezimalzeichen steht im Katalog, nicht hier: es ist eine Eigenschaft der Sprache und
 * keine der Punkterechnung. Ganze Zahlen bleiben ohne Nachkommastelle.
 *
 * Gerundet wird auf **höchstens zwei Nachkommastellen**, nachlaufende Nullen fallen weg. Punkte
 * sind Brüche (siehe `domain/points`), und ein Drittel von elf ist als Gleitkommazahl
 * `3.6666666666666665` — ungerundet stünde das so im Bogen. Gerundet wird ausschließlich hier,
 * beim Anzeigen; gerechnet und gespeichert wird weiter mit dem exakten Bruch.
 */
export function formatPointsIn(value: number, t: Messages): string {
  const gerundet = Math.round(value * 100) / 100;
  return Number.isInteger(gerundet)
    ? String(gerundet)
    : String(gerundet).replace('.', t.format.decimal);
}

/**
 * Formatierer für Punktzahlen in der aktuellen Sprache.
 *
 * Eigener Haken, damit nicht jedes Bauteil `formatPointsIn(wert, t)` schreiben muss — das
 * würde beim Übersehen einer Stelle stillschweigend wieder deutsche Kommas liefern.
 */
export function usePoints(): (value: number) => string {
  const t = useT();
  return (value: number) => formatPointsIn(value, t);
}
